/**
 * The food log for one day: meals in order, each item tappable to change the
 * amount, move it or delete it. The day can be marked complete or incomplete —
 * that decides whether it teaches the expenditure estimate anything.
 */

import { useMemo, useState } from 'react'

import type { FoodRow } from '../db'
import { copyFoods, deleteFood, saveMeal, setDayStatus, updateFood } from '../db/mutations'
import { useDayStatus, useFoodsForDay, useModel, useProfile } from '../db/queries'
import { Training } from '../components/Training'
import { useApp } from '../context'
import { FoodPortion } from '../components/FoodPortion'
import { Sheet } from '../components/Sheet'
import { todayIso } from '../lib/day'
import { addDays, shortDate } from '../lib/days'
import { mealNames, sourceLabel, type FoodItem } from '../lib/foodItem'
import { fmt, fmtKcal, macroColumns, sum } from '../lib/nutrients'

export function LogScreen({ day, setDay }: { day: string; setDay: (d: string) => void }) {
  const { openAdd, user } = useApp()
  const foods = useFoodsForDay(day)
  const status = useDayStatus(day)
  const model = useModel()
  const target = model?.targetOn(day) ?? null
  const profile = useProfile()
  const names = mealNames(profile?.meal_names)
  const [editing, setEditing] = useState<FoodRow | null>(null)
  const [menu, setMenu] = useState<number | null>(null)
  const today = todayIso()

  const totals = useMemo(() => macroColumns(sum((foods ?? []).map((f) => f.nutrients ?? {
    kcal: f.kcal, protein_g: f.protein_g, carbs_g: f.carbs_g, fat_g: f.fat_g }))), [foods])
  const slots = Math.max(names.length, ...(foods ?? []).map((f) => f.meal_slot + 1))

  return (
    <div>
      <div className="row" style={{ marginBottom: '.5rem' }}>
        <button className="secondary" style={{ marginTop: 0, minHeight: 40 }} aria-label="Previous day"
          onClick={() => setDay(addDays(day, -1))}>‹</button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 600 }}>{day === today ? 'Today' : day === addDays(today, -1) ? 'Yesterday' : shortDate(day)}</div>
          {day !== today && <button className="linky" onClick={() => setDay(today)}>Back to today</button>}
        </div>
        <button className="secondary" style={{ marginTop: 0, minHeight: 40 }} aria-label="Next day"
          disabled={day >= today} onClick={() => setDay(addDays(day, 1))}>›</button>
      </div>

      <div className="card" style={{ padding: '.9rem 1rem' }}>
        <div className="row">
          <span><span className="big num">{fmtKcal(totals.kcal)}</span>
            <span className="muted">{target ? ` / ${fmtKcal(target.kcal)} kcal` : ' kcal'}</span></span>
        </div>
        <div className="small" style={{ marginTop: '.3rem' }}>
          <b style={{ color: 'var(--protein)' }}>P</b> {fmt(totals.protein_g)}{target ? `/${Math.round(target.protein_g)}` : ''} g ·{' '}
          <b style={{ color: 'var(--carbs)' }}>C</b> {fmt(totals.carbs_g)}{target ? `/${Math.round(target.carbs_g)}` : ''} g ·{' '}
          <b style={{ color: 'var(--fat)' }}>F</b> {fmt(totals.fat_g)}{target ? `/${Math.round(target.fat_g)}` : ''} g
        </div>
      </div>

      <Training day={day} target={target} isToday={day === today} />

      {Array.from({ length: slots }, (_, slot) => {
        const rows = (foods ?? []).filter((f) => f.meal_slot === slot)
        const kcal = rows.reduce((a, r) => a + r.kcal, 0)
        return (
          <section key={slot}>
            <div className="meal-head">
              <h2>{names[slot] ?? `Meal ${slot + 1}`}</h2>
              <span className="row" style={{ gap: '.6rem' }}>
                {rows.length > 0 && <span className="num muted small">{fmtKcal(kcal)} kcal</span>}
                {rows.length > 0 && <button className="linky" aria-label={`${names[slot]} options`} onClick={() => setMenu(slot)}>···</button>}
              </span>
            </div>
            <div className="card" style={{ padding: '.1rem .4rem' }}>
              {groupRows(rows).map((g) => (
                <div key={g.key}>
                  {g.label && <div className="muted small" style={{ padding: '.4rem .5rem 0' }}>{g.label}</div>}
                  {g.rows.map((r) => (
                    <div key={r.id} className="food-row tappable" role="button" tabIndex={0} onClick={() => setEditing(r)}>
                      <div style={{ minWidth: 0 }}>
                        <div className="name">{r.name}
                          {r.ai_confidence === 'low' && <span className="tag low" style={{ marginLeft: '.4rem' }}>rough</span>}</div>
                        <div className="muted small">{amountText(r)}</div>
                      </div>
                      <span className="num small">{fmtKcal(r.kcal)}</span>
                    </div>
                  ))}
                </div>
              ))}
              <button className="linky" style={{ padding: '.6rem .5rem', fontSize: '.88rem' }}
                onClick={() => openAdd({ day, meal: slot })}>+ Add food</button>
            </div>
          </section>
        )
      })}

      <h2>Day</h2>
      <div className="card" style={{ padding: '.9rem 1rem' }}>
        <div className="pill-row">
          {(['auto', 'complete', 'incomplete'] as const).map((s) => (
            <button key={s} className={`pill ${(status?.status ?? 'auto') === s ? 'on' : ''}`}
              onClick={() => void setDayStatus(day, s, user.id)}>
              {s === 'auto' ? 'Automatic' : s === 'complete' ? 'Complete' : 'Incomplete'}
            </button>
          ))}
        </div>
        <p className="muted small" style={{ marginBottom: 0 }}>
          {(status?.status ?? 'auto') === 'auto'
            ? 'Past days count toward your expenditure unless they look half-logged. Today counts once you mark it complete.'
            : status?.status === 'complete' ? 'Counts toward your expenditure estimate, even if it looks light.'
              : 'Left out of your expenditure estimate — use this for days you didn\'t log fully.'}
        </p>
      </div>

      {editing && <EditEntry row={editing} names={names} onClose={() => setEditing(null)} />}
      {menu !== null && (
        <MealMenu rows={(foods ?? []).filter((f) => f.meal_slot === menu)} name={names[menu] ?? 'Meal'}
          day={day} slot={menu} onClose={() => setMenu(null)} />
      )}
    </div>
  )
}

function groupRows(rows: FoodRow[]) {
  const out: { key: string; label: string | null; rows: FoodRow[] }[] = []
  for (const r of rows) {
    const last = out[out.length - 1]
    if (r.group_id && last && last.key === r.group_id) last.rows.push(r)
    else out.push({ key: r.group_id ?? r.id, label: r.group_id ? r.group_label ?? null : null, rows: [r] })
  }
  return out
}

function amountText(r: FoodRow): string {
  const parts: string[] = []
  if (r.serving_label && r.serving_qty) parts.push(`${fmt(r.serving_qty)} × ${r.serving_label}`)
  else if (r.serving_label && r.grams === null) parts.push(r.serving_label)
  else if (r.grams) parts.push(`${fmt(r.grams)} g`)
  if (r.brand) parts.push(r.brand)
  if (r.source !== 'custom' && r.source !== 'usda') parts.push(sourceLabel(r.source))
  return parts.join(' · ')
}

function EditEntry({ row, names, onClose }: { row: FoodRow; names: string[]; onClose: () => void }) {
  const [busy, setBusy] = useState(false)
  const scalable = !!row.per100 && row.grams !== null && row.grams !== undefined
  const item: FoodItem | null = scalable ? {
    ref: null, source: row.source as FoodItem['source'], name: row.name, brand: row.brand, basis: 'g',
    per100: row.per100!, lastGrams: row.grams, lastServing: row.serving_label, lastQty: row.serving_qty,
    servings: row.serving_label && row.serving_qty ? [{ label: row.serving_label, grams: row.grams! / row.serving_qty }] : [],
  } : null
  return (
    <Sheet title="Edit" onClose={onClose}>
      {item ? (
        <FoodPortion item={item} busy={busy} actionLabel="Save" onSubmit={async (p) => {
          setBusy(true)
          await updateFood(row, { grams: p.grams, serving_label: p.serving_label, serving_qty: p.serving_qty })
          onClose()
        }} />
      ) : (
        <p><b>{row.name}</b><br /><span className="muted small">{fmtKcal(row.kcal)} kcal · P {fmt(row.protein_g)} · C {fmt(row.carbs_g)} · F {fmt(row.fat_g)} g</span></p>
      )}
      <label>Move to</label>
      <div className="pill-row">
        {names.map((n, i) => (
          <button key={n} className={`pill ${row.meal_slot === i ? 'on' : ''}`}
            onClick={async () => { await updateFood(row, { meal_slot: i }); onClose() }}>{n}</button>
        ))}
      </div>
      <button className="secondary" style={{ width: '100%', color: 'var(--danger)', borderColor: 'var(--danger)', marginTop: '1.5rem' }}
        onClick={async () => { await deleteFood(row); onClose() }}>Delete</button>
    </Sheet>
  )
}

function MealMenu({ rows, name, day, slot, onClose }: { rows: FoodRow[]; name: string; day: string; slot: number; onClose: () => void }) {
  const [saveName, setSaveName] = useState(name)
  const [done, setDone] = useState<string | null>(null)
  const today = todayIso()
  return (
    <Sheet title={name} onClose={onClose}>
      {done && <p className="small" style={{ color: 'var(--accent)' }}>✓ {done}</p>}
      <label htmlFor="mm-name">Save as a meal</label>
      <div className="row" style={{ gap: '.5rem' }}>
        <input id="mm-name" value={saveName} onChange={(e) => setSaveName(e.target.value)} />
        <button style={{ marginTop: 0 }} disabled={!saveName.trim()} onClick={async () => {
          await saveMeal(saveName.trim(), rows.map((r) => ({ name: r.name, brand: r.brand, grams: r.grams,
            per100: r.per100, nutrients: r.nutrients ?? { kcal: r.kcal, protein_g: r.protein_g, carbs_g: r.carbs_g, fat_g: r.fat_g },
            serving_label: r.serving_label, serving_qty: r.serving_qty, source: r.source, source_ref: r.source_ref })))
          setDone(`Saved “${saveName.trim()}”`)
        }}>Save</button>
      </div>
      {day !== today && (
        <button className="secondary" style={{ width: '100%' }} onClick={async () => {
          await copyFoods(rows, today, slot)
          setDone('Copied to today')
        }}>Copy to today</button>
      )}
      <button className="secondary" style={{ width: '100%', color: 'var(--danger)', borderColor: 'var(--danger)' }}
        onClick={async () => { for (const r of rows) await deleteFood(r); onClose() }}>Delete all {rows.length} items</button>
    </Sheet>
  )
}
